import React, { useState } from "react";
import { Box, Typography, IconButton, Chip, Divider } from "@mui/material";
import { Add, FiberManualRecord, ReplyAllOutlined, StickyNote2Outlined } from "@mui/icons-material";
import ReactHtmlParser from "html-react-parser";
import { toast } from "react-toastify";
import FormDrawer from "components/Shared/FormDrawer";
import BasicNoteForm from "../../Note/BasicNoteForm";
import { NoteReplies } from "../../Note/NoteReplyList";
import { useAuth } from "hooks";
import { useAddNoteSourceMutation, useGetNotesBySourceQuery } from "store/services/noteApi";

const CompactNoteItem = ({ note, isActive }) => {
  const { getAuthorName, isCustomer, isVendor, isContractor, isDriver } = useAuth();
  const [expanded, setExpanded] = useState(false);
  if (!note || ((isCustomer || isVendor || isContractor || isDriver) && !note.showClient)) return null;

  const [contentBeforeLocation] = note.content.split("Location");
  const authorTypeLabel = { VendorProfile: "Vendor", VendorStaff: "Vendor Staff" }[note?.createdBy?.typeRefSource] || "";
  const replyCount = note?.replies?.length || 0;

  return (
    <Box sx={{ py: 0.75, px: 0.75, borderRadius: 1.5, transition: "background-color 0.15s ease", "&:hover": { bgcolor: "action.hover" } }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
        <StickyNote2Outlined color="primary" sx={{ fontSize: "0.95rem", mt: 0.3 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
              {getAuthorName(note?.createdBy)}
              {authorTypeLabel && <span style={{ color: "gray", fontSize: 10, marginLeft: 4 }}>({authorTypeLabel})</span>}
            </Typography>
            <FiberManualRecord sx={{ color: "grey.400", fontSize: 6 }} />
            <Typography sx={{ fontSize: "0.66rem", color: "text.primary", fontWeight: 600 }}>{new Date(note?.createdAt).toLocaleString()}</Typography>
            {note.showClient && !(isCustomer || isVendor || isContractor || isDriver) && <Chip label="Client" size="small" sx={{ height: 16, fontSize: "0.55rem" }} />}
          </Box>
          {contentBeforeLocation && (
            <Typography sx={{ fontSize: "0.74rem", mt: 0.25 }} className={!expanded ? "line-clamp-2" : ""}>
              {ReactHtmlParser(contentBeforeLocation.trim())}
            </Typography>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
            <IconButton size="small" onClick={() => setExpanded((p) => !p)} sx={{ p: 0.25 }}>
              <ReplyAllOutlined sx={{ fontSize: "0.85rem" }} />
            </IconButton>
            <Typography sx={{ fontSize: "0.65rem", color: "primary.main", cursor: "pointer" }} onClick={() => setExpanded((p) => !p)}>
              {replyCount > 0 ? `${replyCount} repl${replyCount > 1 ? "ies" : "y"}` : "Reply"}
            </Typography>
          </Box>
          {expanded && (
            <Box sx={{ mt: 0.5, ml: -1 }}>
              <NoteReplies noteId={note?._id} replies={note?.replies || []} openReplyBoxById={note?._id} isActive={isActive} showReplies={true} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ShipmentNotesCompact = ({ source, sourceRef, isActive }) => {
  const { isCustomer, isVendor } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const { data, refetch } = useGetNotesBySourceQuery({ source, sourceRef, isActive });
  const [addNote, { isLoading: addingNote }] = useAddNoteSourceMutation();

  const sortedNotes = Array.isArray(data)
    ? [...data].filter((n) => (isCustomer || isVendor ? n.showClient : true)).sort((a, b) => (a?.createdAt > b?.createdAt ? -1 : 1))
    : [];

  const handleAdd = async (formData) => {
    try {
      await addNote({ ...formData, source, sourceRef }).unwrap();
      toast.success("Note added!");
      setShowForm(false);
      refetch();
    } catch (err) { toast.error(err?.data?.message ?? "Couldn't add note!"); }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
        <IconButton size="small" disabled={isActive === false} onClick={() => setShowForm(true)}>
          <Add sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>
      {sortedNotes.length === 0 ? (
        <Typography sx={{ fontSize: "0.74rem", color: "text.secondary", textAlign: "center", py: 2 }}>No notes found.</Typography>
      ) : (
        sortedNotes.map((note) => (
          <CompactNoteItem key={note._id} note={note} isActive={isActive} />
        ))
      )}
      <FormDrawer title="Note" open={showForm} onClose={() => setShowForm(false)}>
        <BasicNoteForm isBusy={addingNote} noteDetail={null} onSubmit={handleAdd} onCancel={() => setShowForm(false)} isShowClient={false} source={source} />
      </FormDrawer>
    </Box>
  );
};

export default ShipmentNotesCompact;